import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Send, Globe, CheckCircle2, AlertCircle, ShieldCheck } from "lucide-react";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    purpose: "",
    message: "",
  });
  
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'success' | 'error' | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Accessing the ID from .env file
    const FORM_ID = process.env.REACT_APP_FORMSPREE_ID;

    try {
      const response = await fetch(`https://formspree.io/f/${FORM_ID}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus("success");
        setFormData({ name: "", email: "", company: "", purpose: "", message: "" });
      } else {
        setStatus("error");
      }
    } catch (error) {
      setStatus("error");
    } finally {
      setLoading(false);
      setTimeout(() => setStatus(null), 5000);
    }
  };

  const inputClasses = "w-full h-12 px-4 border border-slate-200 bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-[#2dd4bf]/20 focus:border-[#2dd4bf] outline-none transition-all rounded-xl text-slate-900 placeholder:text-slate-400";

  return (
    <section id="contact" className="relative py-24 lg:py-32 bg-white overflow-hidden">
      {/* Dynamic Notifications */}
      <AnimatePresence>
        {status === 'success' && (
          <motion.div 
            initial={{ opacity: 0, y: 50 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0 }} 
            className="fixed bottom-10 left-1/2 -translate-x-1/2 z-[110] bg-slate-900 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 border border-[#2dd4bf]/30"
          >
            <CheckCircle2 className="text-[#2dd4bf] w-5 h-5" />
            <span className="font-medium">Transmission Successful: Message Received</span>
          </motion.div>
        )}
        {status === 'error' && (
          <motion.div 
            initial={{ opacity: 0, y: 50 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0 }} 
            className="fixed bottom-10 left-1/2 -translate-x-1/2 z-[110] bg-red-600 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Transmission Error: Please try again.</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-0 w-[600px] h-[600px] rounded-full bg-[#2dd4bf]/5 blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#1e3a5f]/5 blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          
          {/* Left Info Column */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-[#1e3a5f] text-sm font-semibold tracking-widest uppercase mb-4">Secure Channel</p>
            <h2 className="text-4xl md:text-5xl font-light text-slate-900 mb-8 leading-tight">
              Initiate <span className="bg-gradient-to-r from-[#1e3a5f] to-[#2dd4bf] bg-clip-text text-transparent font-medium">Strategic</span> <br />
              Consultation
            </h2>
            <p className="text-slate-600 text-lg mb-12 max-w-md leading-relaxed">
              Connect with our cybersecurity architecture team to discuss clinical data protection, AI threat surface analysis, or investment opportunities.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-5 p-5 rounded-2xl border border-slate-100 bg-slate-50/50">
                <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-[#2dd4bf] shadow-sm">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-tighter">Official Email</p>
                  <p className="text-slate-900 font-medium">ciphergenix@gmail.com</p>
                </div>
              </div>

              <div className="flex items-start gap-5 p-5 rounded-2xl border border-slate-100 bg-slate-50/50">
                <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-[#1e3a5f] shadow-sm shrink-0">
                  <Globe className="w-5 h-5" />
                </div>
                <div className="grid grid-cols-2 gap-4 w-full">
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-tighter">HQ - United States</p>
                    <p className="text-sm text-slate-700 font-medium">Houston</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-tighter">Regional - India</p>
                    <p className="text-sm text-slate-700 font-medium">Pune</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Form Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <form onSubmit={handleSubmit} className="relative bg-white border border-slate-200 p-8 md:p-10 rounded-[32px] shadow-sm space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-500 uppercase ml-1">Your Name</label>
                  <input
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    className={inputClasses}
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-500 uppercase ml-1">Work Email</label>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={inputClasses}
                    placeholder="john@healthcare.com"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-500 uppercase ml-1">Company</label>
                  <input
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleChange}
                    className={inputClasses}
                    placeholder="Entity Name"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-500 uppercase ml-1">Purpose</label>
                  <select
                    name="purpose"
                    value={formData.purpose}
                    onChange={handleChange}
                    className={inputClasses}
                  >
                    <option value="">Select Category</option>
                    <option value="Sales">Sales & Demo</option>
                    <option value="Investment">Investment Inquiry</option>
                    <option value="Partnership">Partnership</option>
                    <option value="Technical">Technical Support</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase ml-1">Secure Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className={`${inputClasses} h-32 py-3 resize-none`}
                  placeholder="Provide context for your inquiry..."
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-[#1e3a5f] hover:bg-[#2dd4bf] text-white font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 group disabled:opacity-70"
              >
                {loading ? "Establishing Connection..." : (
                  <>
                    Submit Secure Inquiry 
                    <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </>
                )}
              </button>
              
              <p className="text-[10px] text-center text-slate-400 uppercase tracking-widest flex items-center justify-center gap-2">
                <ShieldCheck className="w-3 h-3" /> End-to-End Encrypted Transmission
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;